import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../services/api';

const Login = () => {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    // Add state to handle dynamic error messages
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    const handleChange = (e) => {
        setCredentials({ ...credentials, [e.target.name]: e.target.value });
        // Clear error when user starts typing again
        if (errorMessage) setErrorMessage('');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setErrorMessage(''); // Reset error state on new submission
        
        try {
            const res = await API.post('/login.php', credentials);
            if (res.status === 200) {
                localStorage.setItem('user', JSON.stringify(res.data.user));
                navigate('/dashboard');
            }
        } catch (err) {
            // Check if the backend sent a specific error message (like the pending status)
            if (err.response && err.response.data && err.response.data.message) {
                setErrorMessage(err.response.data.message);
            } else {
                // Fallback generic error
                setErrorMessage("Login failed. Please check your credentials.");
            }
        }
    };

    return (
        <div className="login-wrapper">
            <style>{`
                .login-wrapper {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(-45deg, #134628, #0a2415, #1b6339, #134628);
                    background-size: 400% 400%;
                    animation: gradientBG 15s ease infinite;
                    padding: 120px 20px 60px; 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }

                .login-box {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(20px);
                    width: 100%;
                    max-width: 450px;
                    border-radius: 24px;
                    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    animation: slideUpFade 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
                }

                .login-header-section {
                    background: linear-gradient(135deg, #134628 0%, #1c663a 100%);
                    color: white;
                    padding: 50px 20px 40px;
                    text-align: center;
                    position: relative;
                }

                .login-header-section::after {
                    content: '';
                    position: absolute;
                    bottom: 0; left: 0; right: 0;
                    height: 5px;
                    background: linear-gradient(90deg, #ffc300, #ffb703);
                }

                .login-logo-circle {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #ffc300 0%, #ffb703 100%);
                    color: #134628; 
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    font-size: 2.2rem;
                    box-shadow: 0 10px 25px rgba(255, 195, 0, 0.4);
                    animation: pulseGlow 2s infinite;
                }

                .login-header-section h2 {
                    font-weight: 900;
                    letter-spacing: 2px;
                    margin: 0 0 5px 0;
                    font-size: 1.8rem;
                }
                
                .login-header-section p {
                    color: #a0c4ff;
                    margin: 0;
                    font-size: 1rem;
                    font-weight: 300;
                }

                .login-form {
                    padding: 40px 35px 30px;
                }

                /* 🌟 NEW: Error Message Styles */
                .error-alert {
                    background-color: #fee2e2;
                    color: #dc2626;
                    padding: 12px 15px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    text-align: center;
                    border: 1px solid #f87171;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    animation: slideUpFade 0.3s ease;
                }

                .input-group-custom {
                    margin-bottom: 25px;
                    position: relative;
                }

                .input-group-custom label {
                    display: block;
                    margin-bottom: 10px;
                    font-weight: 700;
                    color: #134628; 
                    font-size: 0.95rem;
                }

                .input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .input-wrapper i {
                    position: absolute;
                    left: 15px;
                    color: #a0aab5;
                    font-size: 1.1rem;
                    transition: color 0.3s ease;
                }

                .input-group-custom input {
                    width: 100%;
                    padding: 14px 15px 14px 45px; 
                    border: 2px solid #e1e4e8;
                    border-radius: 12px;
                    font-size: 1rem;
                    background: #fdfdfd;
                    color: #333;
                    transition: all 0.3s ease;
                }

                .input-group-custom input:focus {
                    border-color: #ffc300;
                    outline: none;
                    background: #ffffff;
                    box-shadow: 0 0 0 4px rgba(255, 195, 0, 0.15);
                }

                .input-group-custom input:focus + i,
                .input-wrapper:focus-within i {
                    color: #ffc300;
                }

                .forgot-password-link {
                    display: block;
                    text-align: right;
                    margin-top: -15px;
                    margin-bottom: 20px;
                    font-size: 0.9rem;
                    font-weight: 700;
                    color: #134628;
                    text-decoration: none;
                    transition: color 0.3s ease;
                }

                .forgot-password-link:hover {
                    color: #ffc300;
                }

                .login-submit-btn {
                    width: 100%;
                    background: linear-gradient(45deg, #ffc300, #ffb703);
                    color: #134628; 
                    border: none;
                    padding: 16px;
                    border-radius: 12px;
                    font-weight: 800;
                    font-size: 1.1rem;
                    cursor: pointer;
                    transition: all 0.4s ease;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-top: 5px;
                    box-shadow: 0 8px 20px rgba(255, 195, 0, 0.3);
                }

                .login-submit-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 30px rgba(255, 195, 0, 0.5);
                    background: linear-gradient(45deg, #ffb703, #ffc300);
                }

                .login-footer {
                    text-align: center;
                    padding: 0 30px 40px;
                    font-size: 0.95rem;
                    color: #666;
                }

                .login-footer a {
                    color: #134628; 
                    font-weight: 800;
                    margin-left: 8px;
                    text-decoration: none;
                    transition: color 0.3s;
                    border-bottom: 2px solid transparent;
                }

                .login-footer a:hover {
                    color: #ffc300;
                    border-bottom-color: #ffc300;
                }

                @keyframes gradientBG {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }

                @keyframes slideUpFade {
                    from { opacity: 0; transform: translateY(40px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes pulseGlow {
                    0% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0.6); }
                    70% { box-shadow: 0 0 0 15px rgba(255, 195, 0, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0); }
                }

                @media (max-width: 480px) {
                    .login-wrapper { padding: 100px 15px 40px; }
                    .login-box { border-radius: 20px; }
                    .login-header-section { padding: 40px 15px 30px; }
                    .login-logo-circle { width: 70px; height: 70px; font-size: 1.8rem; }
                    .login-form { padding: 30px 20px 20px; }
                }
            `}</style>

            <div className="login-box">
                <div className="login-header-section">
                    <div className="login-logo-circle">
                        <i className="fas fa-shield-alt"></i>
                    </div>
                    <h2>SHERIAN LOGIN</h2>
                    <p>Secure Alumni Portal</p>
                </div>

                <form className="login-form" onSubmit={handleLogin}>
                    
                    {/* 🌟 NEW: Conditional Error Message Display */}
                    {errorMessage && (
                        <div className="error-alert">
                            <i className="fas fa-exclamation-circle"></i>
                            {errorMessage}
                        </div>
                    )}

                    <div className="input-group-custom">
                        <label htmlFor="email">Email Address</label>
                        <div className="input-wrapper">
                            <i className="fas fa-envelope"></i>
                            <input 
                                id="email"
                                type="email" 
                                name="email" 
                                placeholder="cadet@kskcc.edu.pk"
                                onChange={handleChange} 
                                required 
                            />
                        </div>
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="password">Password</label>
                        <div className="input-wrapper">
                            <i className="fas fa-lock"></i>
                            <input 
                                id="password"
                                type="password" 
                                name="password" 
                                placeholder="upper & lowercase, special ch, number"
                                onChange={handleChange} 
                                required 
                            />
                        </div>
                    </div>

                    <Link to="/forgot-password" className="forgot-password-link">
                        Forgot Password?
                    </Link>

                    <button type="submit" className="login-submit-btn">
                        Sign In <i className="fas fa-sign-in-alt ms-2"></i>
                    </button>
                </form>

                <div className="login-footer">
                    <span>Don't have an account?</span>
                    <Link to="/register">Register as Alumni</Link>
                </div>
            </div>
        </div>
    );
};

export default Login;